import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, Image, Loader2, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminQuizExampleImage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [images, setImages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API}/api/admin/quiz-example-image`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setImages(data.quiz_example_images || []);
        setUpdatedAt(data.updated_at);
      }
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error('File too large. Maximum size is 10MB');
      return;
    }

    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload PNG, JPEG, or WebP');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${API}/api/admin/quiz-example-image/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Upload failed');
      }

      const data = await res.json();
      setImages(data.quiz_example_images || []);
      setUpdatedAt(data.updated_at);
      setCurrentIndex(data.quiz_example_images.length - 1); // Show newly uploaded image
      
      toast.success('Quiz example image uploaded successfully');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (imageUrl) => {
    if (!window.confirm('Are you sure you want to remove this image?')) return;

    try {
      const res = await fetch(`${API}/api/admin/quiz-example-image/delete?image_url=${encodeURIComponent(imageUrl)}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Delete failed');
      }

      // Remove from local state
      const newImages = images.filter(img => img !== imageUrl);
      setImages(newImages);
      if (currentIndex >= newImages.length && newImages.length > 0) {
        setCurrentIndex(newImages.length - 1);
      }
      
      toast.success('Image removed');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm('Are you sure you want to remove ALL quiz example images?')) return;

    try {
      const res = await fetch(`${API}/api/admin/quiz-example-image/delete`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Delete failed');
      }

      setImages([]);
      setCurrentIndex(0);
      toast.success('All images removed');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const getFullImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    if (url.startsWith('/uploads')) return `${API}/api${url}`;
    if (url.startsWith('/')) return `${API}${url}`;
    return `${API}/${url}`;
  };

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#F8FAFD] to-[#E8EEF3] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF9E00]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F8FAFD] to-[#E8EEF3]">
      {/* Header */}
      <div className="bg-white border-b border-[#E8EEF3]">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Button
            onClick={() => navigate('/admin')}
            variant="ghost"
            className="text-[#4A5E75] hover:text-[#1E3A5C] hover:bg-[#E8EEF3] mb-4"
            data-testid="back-to-admin-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Admin
          </Button>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-[#FF9E00]/10 rounded-xl">
              <Image className="w-6 h-6 text-[#FF9E00]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#1E3A5C]">Quiz Example Images</h1>
              <p className="text-[#4A5E75] text-sm">Upload images to show in the Browse Topics carousel</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Image Carousel Preview */}
        <div className="bg-white rounded-2xl border border-[#E8EEF3] p-6 mb-6">
          <h2 className="text-lg font-semibold text-[#1E3A5C] mb-2">Quiz Example Carousel</h2>
          <p className="text-sm text-[#4A5E75] mb-4">
            These images appear at the top of the Browse Topics page as a toggleable carousel.
            Upload multiple images to let users cycle through different quiz examples.
          </p>

          {/* Preview Area */}
          <div className="relative bg-slate-100 rounded-xl overflow-hidden mb-4">
            {images.length > 0 ? (
              <>
                <img 
                  src={getFullImageUrl(images[currentIndex])} 
                  alt={`Quiz Example ${currentIndex + 1}`} 
                  className="w-full h-auto"
                  data-testid="quiz-example-preview"
                />
                
                {/* Navigation (only show if multiple images) */}
                {images.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors"
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors"
                    >
                      <ChevronRight className="w-6 h-6" />
                    </button>
                    
                    {/* Counter */}
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                      {currentIndex + 1} / {images.length}
                    </div>
                  </>
                )}
                
                {/* Delete current image button */}
                <button
                  onClick={() => handleDelete(images[currentIndex])}
                  className="absolute top-4 right-4 w-10 h-10 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center text-white transition-colors"
                  title="Delete this image"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </>
            ) : (
              <div className="aspect-video flex items-center justify-center text-slate-400 p-8">
                <div className="text-center">
                  <Image className="w-12 h-12 mx-auto mb-3" />
                  <p className="text-sm">No images uploaded</p>
                  <p className="text-xs mt-1">Topics page will show cards only</p>
                </div>
              </div>
            )}
          </div>

          {/* Upload Controls */}
          <div className="flex flex-wrap gap-3">
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept="image/png,image/jpeg,image/webp"
              onChange={(e) => handleUpload(e.target.files?.[0])}
              data-testid="quiz-example-file-input"
            />
            
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="bg-[#FF9E00] hover:bg-[#E88D00] text-white"
              data-testid="upload-quiz-example-btn"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Add Image
                </>
              )}
            </Button>

            {images.length > 0 && (
              <Button
                variant="outline"
                onClick={handleDeleteAll}
                className="text-red-500 border-red-200 hover:bg-red-50"
                data-testid="delete-all-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Remove All
              </Button>
            )}
          </div>

          {/* Image thumbnails */}
          {images.length > 1 && (
            <div className="mt-4 flex gap-2 overflow-x-auto pb-2">
              {images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  className={`relative flex-shrink-0 w-24 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                    idx === currentIndex ? 'border-[#FF9E00]' : 'border-transparent'
                  }`}
                >
                  <img 
                    src={getFullImageUrl(img)} 
                    alt={`Thumbnail ${idx + 1}`} 
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Guidelines */}
        <div className="bg-white rounded-2xl border border-[#E8EEF3] p-6">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Image Guidelines</h3>
          <ul className="text-xs text-slate-500 space-y-1">
            <li>• Supported formats: PNG, JPEG, WebP</li>
            <li>• Maximum file size: 10MB per image</li>
            <li>• Maximum images: 10</li>
            <li>• Recommended: Quiz screenshots showing questions and answers</li>
            <li>• The full image will be shown (no cropping)</li>
          </ul>
        </div>

        {updatedAt && (
          <p className="text-xs text-slate-400 text-center mt-6">
            Last updated: {new Date(updatedAt).toLocaleString()}
          </p>
        )}
      </div>
    </div>
  );
}
